/**
 * Varselklokkas og sidemenyens egne spørringer (KOI Del 4).
 *
 * ---------------------------------------------------------------------------------------------
 * DE GJENBRUKER OVERSIKTENS `unreadCountQueryOptions` OG `unreadNotificationsQueryOptions`
 * framfor å lage sine egne: det er de SAMME radene og den samme nøkkelen, og en andre nøkkel over
 * det samme ville vært den første til å bli foreldet — og den som viser «3» i klokka mens panelet
 * viser to rader.
 *
 * Det som er nytt her er uleste-TELLINGENE (`GET /v1/threads/unread-counts`), som klokka ikke
 * bruker, men sidemenyen gjør: Innboks og Oppgaver får hvert sitt merke.
 */
import {
  inboxUnreadCountsResponseSchema,
  notificationListResponseSchema,
  type NotificationListResponse,
  linkSuggestResponseSchema,
  mentionSuggestResponseSchema,
  type InboxUnreadCountsResponse,
  type LinkSuggestResponse,
  type MentionSuggestResponse,
} from '@tvitvi/shared';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

/**
 * Sidemenyens to tall, i ett kall.
 *
 * `CACHE.volatile`, som varselmerket: et tall som står feil i et minutt er et tall ingen stoler
 * på. Sanntidsstrømmen invaliderer den uansett i det noe skjer — dette er gulvet for en fane som
 * har stått åpen uten at strømmen nådde fram.
 */
export function inboxUnreadCountsQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.inboxUnreadCounts(orgId),
    queryFn: ({ signal }): Promise<InboxUnreadCountsResponse> =>
      apiRequest({
        path: '/threads/unread-counts',
        schema: inboxUnreadCountsResponseSchema,
        signal,
      }),
    ...CACHE.volatile,
  });
}

/**
 * VELGEREN `#` ÅPNER — oppgaver, programposter og ressurser i én liste.
 *
 * NØKKELEN INNEHOLDER SØKETEKSTEN, så hvert tastetrykk er sin egen nøkkel og React Query husker
 * dem: å skrive en bokstav for mye og slette den igjen koster ingen ny runde. Det er den samme
 * formen enhver typeahead i huset har.
 *
 * `CACHE.list` og ikke `volatile`: en oppgaveliste endrer seg ikke mens noen skriver en melding,
 * og en velger som henter på nytt mellom to tastetrykk flimrer.
 */
export function linkSuggestQueryOptions(orgId: string, q: string) {
  return queryOptions({
    queryKey: qk.linkSuggest(orgId, q),
    queryFn: ({ signal }): Promise<LinkSuggestResponse> =>
      apiRequest({
        path: '/links/suggest',
        query: q === '' ? {} : { q },
        schema: linkSuggestResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * VELGEREN `@` ÅPNER — hele huset, med årgangens rolle og avdeling (rettelse B3).
 *
 * `GET /v1/mentions/suggest` har svart på dette siden KOI Del 4 og ingen skjerm spurte den: begge
 * skrivefeltene tilbød bare samtalens egne folk. Det var en regel med en grunn — en omtale får bare
 * navngi noen som ER i samtalen — men den løste feil problem: den som skriver «spør @Kari» mener
 * Kari, og at hun ikke er i tråden ennå er noe man ORDNER. Se `namedOutsiders`.
 *
 * `editionId` når tråden henger på en årgang: da bærer hver rad rollen og avdelingen personen har
 * DET året, som er det som skiller to like navn fra hverandre.
 *
 * Nøkkelen inneholder søketeksten, som `#`-velgerens — hvert tastetrykk er sin egen nøkkel, og å
 * skrive en bokstav for mye og slette den igjen koster ingen ny runde.
 */
export function mentionSuggestQueryOptions(orgId: string, q: string, editionId?: string) {
  return queryOptions({
    queryKey: qk.mentionSuggest(orgId, q, editionId ?? null),
    queryFn: ({ signal }): Promise<MentionSuggestResponse> =>
      apiRequest({
        path: '/mentions/suggest',
        query: {
          ...(q === '' ? {} : { q }),
          ...(editionId === undefined ? {} : { editionId }),
        },
        schema: mentionSuggestResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * F45: HISTORIKKEN — varslene man har lest, nyeste først, side for side. Den samme ruta som de
 * uleste (`unread=false`), og nøkkelen henger under `qk.notificationList(orgId, false)`, så å merke
 * noe lest (som invaliderer `qk.notifications`) også friske opp denne.
 */
export function readNotificationsInfiniteQueryOptions(orgId: string, limit = 50) {
  return infiniteQueryOptions({
    queryKey: [...qk.notificationList(orgId, false), 'pages', limit] as const,
    queryFn: ({ pageParam, signal }): Promise<NotificationListResponse> =>
      apiRequest({
        path: '/notifications',
        organizationId: orgId,
        query: {
          sort: 'recent',
          direction: 'desc',
          limit,
          unread: false,
          ...(pageParam === null ? {} : { cursor: pageParam }),
        },
        schema: notificationListResponseSchema,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: NotificationListResponse) => (last.hasMore ? last.nextCursor : null),
    ...CACHE.list,
  });
}
